import { defaultStoreUtils } from '@databases/redux';
import { getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getValueId } from '@databases/utils/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type CommentThreadEvent } from '@canvas/comments/comment/store';
import { getCommentStore } from '@canvas/comments/state/comment-store';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';
import { currentDrawerPanel } from '@canvas/sidebar-panel-state';

import { type OpenThreadContextData } from '../context/CommentContext';
import { useCloseWithDraftCheck } from '../hooks/useCloseWithDraftCheck';
import {
  useCommentActionsContext,
  useCommentStateContext,
} from '../hooks/useCommentContext';
import { type Thread, type ThreadWithValueId } from '../type';
import {
  getNextThreadData,
  getPreviousThreadData,
  handleThreadNavigation,
} from '../utils';

import CommentThread from './CommentThread';
import NewCommentThread from './NewCommentThread';

interface CommentThreadProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  commentToggled: boolean;
  threads: Thread[] | undefined;
  openThread: OpenThreadContextData;
  create?: boolean;
  setIsDeleting: (isDeleting: boolean) => void;
  handleClose: () => void;
}

const CommentThreads = ({
  contentId,
  fieldId,
  entryId,
  commentToggled,
  threads,
  openThread,
  create,
  setIsDeleting,
  handleClose,
}: CommentThreadProps) => {
  const [selectedThread, setSelectedThread] = useState<ThreadWithValueId>(undefined);
  const [isCreate, setIsCreate] = useState(!!create);

  const { setOpenThread, setFocusedCommentThreadId } = useCommentActionsContext();
  const { allThreads } = useCommentStateContext();

  const store = getCommentStore(undefined, currentHostFrameContext);
  const valueId = getValueId(entryId, fieldId);
  const selectionsActions = getService('SelectionsActions');

  const [currentUserId, stringRepresentation] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const field = getDatabaseFieldRequired(database, fieldId);
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getDatabaseValue(database, field, entry);
      const currentUserId = selectCurrentUserId(state);

      const stringRepresentation =
        value.type !== DatabaseFieldType.ATTACHMENT
          ? getStringRepresentation({ database, field, entry, value }, state)
          : undefined;

      return [currentUserId, stringRepresentation, allThreads] as const;
    }
  );

  const focusedThreadIndex = useMemo(
    () =>
      openThread.threadId
        ? allThreads.findIndex((thread) => thread?.threadId === openThread.threadId)
        : allThreads.findIndex((thread) => thread?.valueId === valueId),
    [allThreads, openThread.threadId, valueId]
  );

  const focusedThreadIndexFound = useMemo(
    () => focusedThreadIndex !== -1,
    [focusedThreadIndex]
  );

  const [selectedThreadIndex, setSelectedThreadIndex] = useState<number>(
    focusedThreadIndexFound ? focusedThreadIndex : 0
  );

  const handleCloseWithDraftCheck = useCloseWithDraftCheck({
    handleClose,
    store,
    threadId: selectedThread?.threadId,
  });

  useEffect(() => {
    selectionsActions.selectValues([{ entryId, fieldId }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles setting first thread on a cell that has none
  useEffect(() => {
    if (
      focusedThreadIndex !== undefined &&
      focusedThreadIndexFound &&
      threads?.length === 1
    ) {
      setSelectedThreadIndex(focusedThreadIndex);
    }
  }, [focusedThreadIndex, focusedThreadIndexFound, threads]);

  useEffect(() => {
    if (!create && threads?.length) {
      setSelectedThread(allThreads[selectedThreadIndex]);
    } else {
      setSelectedThread(undefined);
      setIsCreate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentThread = allThreads[selectedThreadIndex];
    if (
      currentThread?.threadId === selectedThread?.threadId &&
      currentThread?.versionNumber !== selectedThread?.versionNumber
    ) {
      setSelectedThread(currentThread);
    }
  }, [
    allThreads,
    selectedThread?.threadId,
    selectedThread?.versionNumber,
    selectedThreadIndex,
  ]);

  const onNextThread = useCallback(() => {
    const totalThreads = allThreads.length;

    if (totalThreads === 0) {
      return;
    }

    const { nextThreadIndex, nextThread } = getNextThreadData(
      selectedThreadIndex,
      totalThreads,
      allThreads
    );

    handleThreadNavigation(
      nextThread,
      nextThreadIndex,
      valueId,
      openThread.panel,
      setSelectedThreadIndex,
      setFocusedCommentThreadId,
      setOpenThread,
      setSelectedThread
    );
  }, [
    allThreads,
    openThread.panel,
    selectedThreadIndex,
    setFocusedCommentThreadId,
    setOpenThread,
    valueId,
  ]);

  const onPreviousThread = useCallback(() => {
    const totalThreads = allThreads.length;

    if (totalThreads === 0) {
      return;
    }

    const { previousThreadIndex, previousThread } = getPreviousThreadData(
      selectedThreadIndex,
      totalThreads,
      allThreads
    );

    handleThreadNavigation(
      previousThread,
      previousThreadIndex,
      valueId,
      openThread.panel,
      setSelectedThreadIndex,
      setFocusedCommentThreadId,
      setOpenThread,
      setSelectedThread
    );
  }, [
    allThreads,
    openThread.panel,
    selectedThreadIndex,
    setFocusedCommentThreadId,
    setOpenThread,
    valueId,
  ]);

  const onThreadCreate = (evt: CommentThreadEvent) => {
    if (evt.type === 'threadCreated') {
      setIsCreate(false);

      if (currentDrawerPanel.panel === 'commentsPanel') {
        waitForContentAPI().then((canvasFrameAPI) => {
          canvasFrameAPI?.confluenceAPI?.comments?.triggerCommentPanelRefresh?.();
          canvasFrameAPI?.confluenceAPI?.comments?.selectCommentThread?.(evt.externalId);
        });
      }

      setOpenThread({
        valueId: getValueId(entryId, fieldId),
        create: false,
        panel: openThread.panel,
        threadId: evt.externalId,
      });

      setSelectedThread({
        valueId: getValueId(entryId, fieldId),
        threadId: evt.externalId,
        commentCount: 1,
        isResolved: false,
        versionNumber: 0,
      });

      if (threads) {
        // If threads exist on the current cell
        setSelectedThreadIndex(selectedThreadIndex + threads.length);
      } else if (allThreads.length === 0) {
        // If no threads exist at all
        setSelectedThreadIndex(0);
      }
    }
  };

  // Only load comment thread if theres a thread OR if we're creating
  if (focusedThreadIndexFound || (!focusedThreadIndexFound && isCreate)) {
    return isFeatureFlagEnabled('consolidatedComments') ? (
      <NewCommentThread
        contentId={contentId}
        fieldId={fieldId}
        entryId={entryId}
        commentToggled={commentToggled}
        thread={selectedThread}
        currentUserId={currentUserId}
        isCreate={isCreate}
        setIsDeleting={setIsDeleting}
        onThreadCreate={onThreadCreate}
        threadNavigationOpts={
          threads?.length && allThreads?.length && !create
            ? {
                onNextThread,
                onPreviousThread,
              }
            : undefined
        }
        handleClose={handleCloseWithDraftCheck}
      />
    ) : (
      <CommentThread
        contentId={contentId}
        fieldId={fieldId}
        entryId={entryId}
        commentToggled={commentToggled}
        thread={selectedThread}
        currentUserId={currentUserId}
        isCreate={isCreate}
        stringRepresentation={stringRepresentation}
        setIsDeleting={setIsDeleting}
        onThreadCreate={onThreadCreate}
        threadNavigationOpts={
          threads?.length && allThreads?.length && !create
            ? {
                currentThreadNumber: selectedThreadIndex + 1,
                threadCount: allThreads.length,
                onNextThread,
                onPreviousThread,
              }
            : undefined
        }
        handleClose={handleCloseWithDraftCheck}
      />
    );
  }
};

export default CommentThreads;
